/**
 * 顏色推薦 — 五行 / 生命靈數 / 功效 / 直覺
 */

const API_BASE = process.env.REACT_APP_API_BASE || '';

// ==================== 五行 → 顏色 ====================
export const WUXING_COLOR_MAP = {
  '木': { colors: ['綠色', '青色'], trait: '生機成長、創意靈感' },
  '火': { colors: ['紅色', '紫色'], trait: '熱情活力、行動力' },
  '土': { colors: ['黃色', '橙色', '棕色'], trait: '穩定踏實、財運事業' },
  '金': { colors: ['白色', '金色', '銀色'], trait: '純淨明亮、領導氣質' },
  '水': { colors: ['黑色', '藍色'], trait: '智慧流動、溝通表達' },
};

// ==================== 生命靈數 → 顏色 ====================
export const NUMEROLOGY_COLOR_MAP = {
  1: { title: '開創者', colors: ['紅色', '金色'], trait: '領導力與行動力' },
  2: { title: '協調者', colors: ['橙色', '粉色'], trait: '和諧與包容' },
  3: { title: '表達者', colors: ['黃色', '金色'], trait: '創意與自信' },
  4: { title: '建築家', colors: ['綠色', '藍色'], trait: '穩定與安全感' },
  5: { title: '探索者', colors: ['藍色', '紫色'], trait: '自由與冒險' },
  6: { title: '關懷者', colors: ['粉色', '綠色'], trait: '愛與責任' },
  7: { title: '思想家', colors: ['紫色', '白色'], trait: '智慧與內省' },
  8: { title: '成就者', colors: ['黑色', '深紅'], trait: '權威與財富' },
  9: { title: '智者', colors: ['金色', '紫色'], trait: '博愛與智慧' },
};

// ==================== 願望／狀態 → 顏色 ====================
// 以人為本的心願語氣，讓客人感覺被關心、被在乎
export const EFFECT_COLOR_MAP = {
  '財運：想更有底氣': { colors: ['黃色', '綠色'], icon: '💰' },
  '桃花：渴望被愛': { colors: ['粉色', '橙色'], icon: '💗' },
  '防護：想要安全感': { colors: ['黑色', '紫色'], icon: '🛡️' },
  '健康：照顧好自己': { colors: ['綠色', '白色'], icon: '💚' },
  '專注：想沉澱靜心': { colors: ['藍色', '紫色'], icon: '🎯' },
};

// ==================== 直覺選色 ====================
export const INTUITIVE_COLOR_MAP = {
  'pink_gold': { label: '粉金溫柔', colors: ['粉色', '金色', '白色'], description: '溫暖柔美，充滿女性能量' },
  'purple_blue': { label: '紫藍夢幻', colors: ['紫色', '藍色', '銀色'], description: '神秘深邃，激發直覺靈感' },
  'forest_green': { label: '森林綠意', colors: ['綠色', '棕色', '白色'], description: '自然療癒，帶來平靜安寧' },
  'black_white': { label: '黑白極簡', colors: ['黑色', '白色'], description: '俐落時尚，平衡陰陽能量' },
  'warm_sunset': { label: '溫暖夕陽', colors: ['橙色', '紅色', '金色'], description: '活力溫暖，激發熱情行動力' },
};

/**
 * 從 API 獲取動態水晶推薦
 * @param {string[]} colors - 需要的顏色列表
 * @param {number} count - 每種顏色推薦數量
 * @returns {object} - { '綠色': ['綠幽靈', '綠東陵'], ... }
 */
export async function fetchCrystalRecommendations(colors, count = 3) {
  try {
    const res = await fetch(`${API_BASE}/api/crystal-colors/recommend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ colors, count }),
    });
    if (!res.ok) throw new Error('推薦請求失敗');
    const data = await res.json();
    return data.recommendations || {};
  } catch (err) {
    console.warn('水晶推薦 API 失敗:', err);
    return {};
  }
}

// 相生：生我者（母）。水生木、木生火、火生土、土生金、金生水
export const WUXING_GENERATOR = { '木': '水', '火': '木', '土': '火', '金': '土', '水': '金' };
// 相剋：剋我者。金剋木、水剋火、木剋土、火剋金、土剋水
export const WUXING_CONTROLLER = { '木': '金', '火': '水', '土': '木', '金': '火', '水': '土' };

/**
 * 根據五行分析結果推薦顏色（納入相生相剋）
 * - 補益某元素時，連同「生我者（相生）」一起推薦——母生子，更有效補強
 * - 「剋我者（相剋）」會削弱該元素，列為宜避免
 */
export function getWuxingRecommendation(wuxingResult, strategy) {
  const { analysis } = wuxingResult;
  const targetElements = strategy === 'supplement' ? analysis.weakest : analysis.strongest;

  let elements = targetElements;
  if (!elements || elements.length === 0) {
    const scores = wuxingResult.scores;
    const sorted = Object.entries(scores).sort((a, b) =>
      strategy === 'supplement' ? a[1] - b[1] : b[1] - a[1]
    );
    elements = [sorted[0][0]];
  }

  const seen = new Set();
  const recommendations = [];
  const avoidSet = new Set();
  for (const el of elements) {
    if (!seen.has(el)) {
      seen.add(el);
      recommendations.push({ element: el, role: 'primary', ...WUXING_COLOR_MAP[el] });
    }
    // 相生：把「生我者」也加入推薦
    const gen = WUXING_GENERATOR[el];
    if (gen && !seen.has(gen)) {
      seen.add(gen);
      recommendations.push({ element: gen, role: 'generate', ...WUXING_COLOR_MAP[gen] });
    }
    // 相剋：剋我者會削弱此元素 → 宜避免
    const ctrl = WUXING_CONTROLLER[el];
    if (ctrl) avoidSet.add(ctrl);
  }
  // 避免清單不與推薦衝突
  const avoid = [...avoidSet]
    .filter((e) => !seen.has(e))
    .map((el) => ({ element: el, ...WUXING_COLOR_MAP[el] }));

  return {
    strategy,
    strategyLabel: strategy === 'supplement' ? '補足較弱的元素' : '強化突出的優勢',
    elements: recommendations.map((r) => r.element),
    recommendations,
    avoid,
  };
}

/**
 * 根據生命靈數推薦顏色
 */
export function getNumerologyRecommendation(numerologyResult) {
  const lifePathValue = numerologyResult.coreNumbers.lifePath.value;
  const rec = NUMEROLOGY_COLOR_MAP[lifePathValue] || NUMEROLOGY_COLOR_MAP[1];

  const missingRecs = (numerologyResult.missingInsights || []).slice(0, 2).map((m) => {
    const numRec = NUMEROLOGY_COLOR_MAP[m.digit];
    return { digit: m.digit, title: m.title, colors: numRec?.colors || [], suggestion: m.suggestion };
  });

  return {
    lifePath: { value: lifePathValue, ...rec },
    missingRecommendations: missingRecs,
  };
}

/**
 * 根據功效推薦顏色
 */
export function getEffectRecommendation(effect) {
  return EFFECT_COLOR_MAP[effect] || null;
}
